import { Component } from '@theme/component';

const SWIPE_MS = 260;

class DevWhatsInsideComponent extends Component {
  activeIndex = 0;

  connectedCallback() {
    super.connectedCallback();
    this.thumbs = [...this.querySelectorAll('.dev-whats-inside-thumb')];
    if (!this.thumbs.length) return;

    // Cache the main-image URLs and keep decoded copies so subsequent swaps feel instant
    this.imageCache = new Map();
    this.thumbs.forEach((thumb) => {
      const url = thumb.dataset.image;
      if (!url || this.imageCache.has(url)) return;
      const img = new Image();
      img.src = url;
      // Kick off decode when supported; ignore rejection (e.g. cancellations)
      if (typeof img.decode === 'function') img.decode().catch(() => {});
      this.imageCache.set(url, img);
    });

    this.setActive(0, { animate: false });
    this.lockMaxHeights();

    this._onResize = this._debounce(() => this.lockMaxHeights(), 150);
    window.addEventListener('resize', this._onResize);
  }

  disconnectedCallback() {
    if (this._onResize) window.removeEventListener('resize', this._onResize);
    super.disconnectedCallback?.();
  }

  // Pin heading-wrap and left-column min-height to the tallest block so there's
  // no layout shift when switching items (accounts for headings that wrap to a 2nd row).
  lockMaxHeights() {
    const heading = this.refs.heading;
    const headingWrap = this.refs.headingWrap;
    const content = this.refs.content;
    const subtitle = this.refs.subtitle;
    const description = this.refs.description;
    if (!heading || !headingWrap || !content) return;

    headingWrap.style.minHeight = '';
    content.style.minHeight = '';

    const prevPos = heading.style.position;
    const prevDisplay = heading.style.display;
    heading.style.position = 'static';
    heading.style.display = 'block';

    let maxHeadingH = 0;
    let maxContentH = 0;

    this.thumbs.forEach((thumb) => {
      heading.textContent = thumb.dataset.heading || '';
      if (subtitle) subtitle.textContent = thumb.dataset.subtitle || '';
      if (description) description.textContent = thumb.dataset.description || '';

      maxHeadingH = Math.max(maxHeadingH, heading.offsetHeight);
      maxContentH = Math.max(maxContentH, content.offsetHeight);
    });

    heading.style.position = prevPos;
    heading.style.display = prevDisplay;

    headingWrap.style.minHeight = maxHeadingH + 'px';
    content.style.minHeight = maxContentH + 'px';

    const active = this.thumbs[this.activeIndex];
    if (active) {
      heading.textContent = active.dataset.heading || '';
      if (subtitle) subtitle.textContent = active.dataset.subtitle || '';
      if (description) description.textContent = active.dataset.description || '';
    }
  }

  _debounce(fn, wait) {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn(...args), wait);
    };
  }

  selectItem(event) {
    const thumb = event.target.closest('.dev-whats-inside-thumb');
    if (!thumb) return;
    const index = this.thumbs.indexOf(thumb);
    if (index >= 0 && index !== this.activeIndex) this.setActive(index);
  }

  prev() {
    if (this.activeIndex > 0) this.setActive(this.activeIndex - 1);
  }

  next() {
    if (this.activeIndex < this.thumbs.length - 1) this.setActive(this.activeIndex + 1);
  }

  setActive(index, { animate = true } = {}) {
    const thumb = this.thumbs[index];
    if (!thumb) return;

    const direction = index < this.activeIndex ? 'back' : 'forward';
    this.activeIndex = index;

    this.thumbs.forEach((t, i) => t.classList.toggle('is-active', i === index));

    const heading = this.refs.heading;
    const subtitle = this.refs.subtitle;
    const description = this.refs.description;
    const img = this.refs.mainImage;
    const nextUrl = thumb.dataset.image || '';

    const applyText = () => {
      if (heading) heading.textContent = thumb.dataset.heading || '';
      if (subtitle) subtitle.textContent = thumb.dataset.subtitle || '';
      if (description) description.textContent = thumb.dataset.description || '';
    };

    // Some slides need a smaller image render (e.g. the Power Adapter slide
    // ships at a different intrinsic aspect than the boots). Toggle the
    // dev-whats-inside__image--scaled class so CSS controls the size.
    const slideHeading = (thumb.dataset.heading || '').trim().toLowerCase();
    const shouldScale = slideHeading === 'power adapter';

    const applyImage = () => {
      if (!img) return;
      img.classList.toggle('dev-whats-inside__image--scaled', shouldScale);
      if (nextUrl) img.src = nextUrl;
    };

    if (animate && heading) {
      const leaveClass = direction === 'back' ? 'is-leaving-back' : 'is-leaving';
      const enterClass = direction === 'back' ? 'is-entering-back' : 'is-entering';

      heading.classList.add(leaveClass);
      if (img && nextUrl) img.classList.add('is-switching');
      setTimeout(() => {
        applyText();
        applyImage();

        heading.classList.remove(leaveClass);
        heading.classList.add(enterClass);
        void heading.offsetWidth;
        heading.classList.remove(enterClass);

        if (img && nextUrl) {
          // Image is almost certainly cached, so remove the fade-out immediately
          const clear = () => img.classList.remove('is-switching');
          if (img.complete && img.naturalWidth > 0) clear();
          else img.addEventListener('load', clear, { once: true });
        }
      }, SWIPE_MS);
    } else {
      applyText();
      applyImage();
    }

    if (this.refs.prevBtn) {
      this.refs.prevBtn.disabled = index === 0;
      this.refs.prevBtn.classList.toggle('opacity-20', index === 0);
    }
    if (this.refs.nextBtn) {
      this.refs.nextBtn.disabled = index === this.thumbs.length - 1;
      this.refs.nextBtn.classList.toggle('opacity-20', index === this.thumbs.length - 1);
    }
  }
}

if (!customElements.get('dev-whats-inside-component')) {
  customElements.define('dev-whats-inside-component', DevWhatsInsideComponent);
}
